import React, { useEffect, useState } from 'react'
import { Alert, Checkbox, InputNumber, Modal, Space, Typography, message } from 'antd'
import dayjs from 'dayjs'
import { get } from '../../lib/request'
import { ReferrerItem, errMsg } from '../../lib/api'

const { Text } = Typography

/** referrer_export（2026-11-04）：单次导出上限，对齐后端 admin/referrers.ts::REFERRER_EXPORT_MAX */
export const REFERRER_EXPORT_MAX = 5000
/** 「导出前 N 条」默认条数 */
const DEFAULT_EXPORT_COUNT = 100

/** 导出列定义：由列表页提供（复用列表的标签映射/方案名/资质判定），弹窗只负责请求与写表 */
export interface ExportColumn {
  title: string
  /** Excel 列宽（字符数） */
  width: number
  /** number → 数值单元格；其余一律文本（手机号/推荐码/协议编号不被 Excel 转成数字） */
  type?: 'number'
  value: (r: ReferrerItem) => string | number
}

/** GET /admin/referrers/export 响应 data（对齐后端 referrers.ts GET /export） */
interface ExportResult {
  total: number
  exported: number
  truncated: boolean
  items: ReferrerItem[]
}

interface Props {
  open: boolean
  onClose: () => void
  /** 当前列表总条数（用于默认值与上限提示） */
  total: number
  /** 当前筛选 + 排序参数（与列表请求同一份，不含分页） */
  params: Record<string, string | number>
  columns: ExportColumn[]
}

/** 推荐人导出弹窗：按当前排序导出前 N 条或全部，生成 .xlsx 由浏览器下载 */
const ExportModal: React.FC<Props> = ({ open, onClose, total, params, columns }) => {
  const maxCount = Math.max(1, Math.min(total, REFERRER_EXPORT_MAX))
  const [exportAll, setExportAll] = useState(false)
  const [count, setCount] = useState<number | null>(Math.min(DEFAULT_EXPORT_COUNT, maxCount))
  const [exporting, setExporting] = useState(false)

  // 每次打开按最新 total 重置默认值
  useEffect(() => {
    if (open) {
      setExportAll(false)
      setCount(Math.min(DEFAULT_EXPORT_COUNT, maxCount))
    }
  }, [open, maxCount])

  const handleOk = async () => {
    if (exporting) return
    if (!exportAll && (!count || count < 1)) {
      message.warning('请输入导出条数')
      return
    }
    setExporting(true)
    try {
      const data = await get<ExportResult>('/admin/referrers/export', {
        params: { ...params, ...(exportAll ? { all: 1 } : { limit: count }) },
      })
      if (!data.items?.length) {
        message.info('当前筛选条件下没有可导出的数据')
        return
      }
      // 按需加载 xlsx 生成库（独立 chunk，不进首屏包）
      const { default: writeXlsxFile } = await import('write-excel-file/browser')
      const header = columns.map((c) => ({ value: c.title, fontWeight: 'bold' as const }))
      const rows = data.items.map((r) =>
        columns.map((c) => {
          const v = c.value(r)
          return c.type === 'number'
            ? { value: Number(v) || 0, type: Number }
            : { value: String(v ?? ''), type: String }
        }),
      )
      const fileName = `推荐人_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`
      await writeXlsxFile([header, ...rows], {
        columns: columns.map((c) => ({ width: c.width })),
        sheet: '推荐人',
      }).toFile(fileName)
      message.success(
        data.truncated
          ? `已导出 ${data.exported} 条（单次上限 ${REFERRER_EXPORT_MAX} 条，共 ${data.total} 条）`
          : `已导出 ${data.exported} 条`,
      )
      onClose()
    } catch (err) {
      message.error(errMsg(err, '导出失败'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <Modal
      title="导出推荐人"
      open={open}
      onCancel={() => { if (!exporting) onClose() }}
      onOk={handleOk}
      okText="导出 Excel"
      confirmLoading={exporting}
      okButtonProps={{ disabled: total === 0, style: { minHeight: 44 } }}
      cancelButtonProps={{ disabled: exporting, style: { minHeight: 44 } }}
      maskClosable={!exporting}
      width="min(420px, calc(100vw - 32px))"
      destroyOnClose
    >
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        <Text type="secondary">按列表当前的筛选条件和排序顺序导出。</Text>
        <Space wrap align="center">
          <Text>导出前</Text>
          <InputNumber
            min={1}
            max={maxCount}
            precision={0}
            value={count}
            disabled={exportAll || total === 0}
            onChange={(v) => setCount(v)}
            style={{ width: 120, minHeight: 44, display: 'inline-flex', alignItems: 'center' }}
          />
          <Text>条</Text>
        </Space>
        <Checkbox
          checked={exportAll}
          disabled={total === 0}
          onChange={(e) => setExportAll(e.target.checked)}
          style={{ minHeight: 44, display: 'flex', alignItems: 'center' }}
        >
          全部导出（共 {total} 条）
        </Checkbox>
        {total > REFERRER_EXPORT_MAX && (
          <Alert
            type="warning"
            showIcon
            message={`单次最多导出 ${REFERRER_EXPORT_MAX} 条，超出部分请缩小筛选范围后分批导出。`}
          />
        )}
        {total === 0 && <Alert type="info" showIcon message="当前筛选条件下没有数据。" />}
      </Space>
    </Modal>
  )
}

export default ExportModal
